import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { content } from '../content/agency';

export const AgencyPartners = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* 1) HERO / OVERVIEW */}
        <section className="text-center mb-20">
          <div className="mb-8">
            <img 
              src="/logo/logo-glossy.png" 
              alt="RiverBlue Logo" 
              className="w-24 h-24 mx-auto mb-6 drop-shadow-[0_0_20px_rgba(59,130,246,0.4)] drop-shadow-[0_0_40px_rgba(59,130,246,0.3)] filter brightness-110 contrast-105"
              style={{
                filter: 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.4)) drop-shadow(0 0 40px rgba(59, 130, 246, 0.3)) brightness(1.1) contrast(1.05) saturate(1.1)',
              }}
            />
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent leading-[1.1] pb-1 overflow-visible">
            {content.hero.title}
          </h1>
          <p className="text-xl sm:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {content.hero.subtitle}
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {content.hero.badges.map((badge, index) => (
              <Badge key={index} variant="primary">
                {badge}
              </Badge>
            ))}
          </div>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto">
            {content.hero.minimumFit}
          </p>
        </section>

        {/* 2) HOW AGENCIES USE RIVERBLUE */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            How Agencies Use RiverBlue
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            {/* Branded Card */}
            <Card className="p-8 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                {content.howAgenciesUseIt.branded.title}
              </h3>
              <ul className="space-y-3">
                {content.howAgenciesUseIt.branded.bullets.map((bullet, index) => (
                  <li key={index} className="text-base text-gray-700 flex items-start">
                    <span className="mr-3 text-blue-600 font-semibold">•</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* White-Label Card */}
            <Card className="p-8 hover:shadow-lg transition-shadow border-2 border-blue-100 bg-gradient-to-br from-blue-50/50 to-white">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                {content.howAgenciesUseIt.whiteLabel.title}
              </h3>
              <ul className="space-y-3">
                {content.howAgenciesUseIt.whiteLabel.bullets.map((bullet, index) => (
                  <li key={index} className="text-base text-gray-700 flex items-start">
                    <span className="mr-3 text-blue-600 font-semibold">•</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
          <p className="text-sm text-gray-600 text-center italic max-w-2xl mx-auto">
            {content.howAgenciesUseIt.note}
          </p>
        </section>

        {/* 3) WHAT CLIENTS SEE */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {content.whatClientsSee.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {content.whatClientsSee.outcomes.map((outcome, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-md transition-shadow">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                  <div className="w-6 h-6 rounded-full bg-blue-500"></div>
                </div>
                <p className="text-base text-gray-700 font-medium">{outcome}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* 4) SECURITY & DATA SAFETY */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {content.security.title}
          </h2>
          <Card className="p-8 max-w-3xl mx-auto">
            <ul className="space-y-4 mb-4">
              {content.security.assurances.map((assurance, index) => (
                <li key={index} className="text-base text-gray-700 flex items-start">
                  <span className="mr-4 flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  </span>
                  <span>{assurance}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-600 text-center italic mt-6">
              Used by agencies managing multiple client CRMs.
            </p>
          </Card>
        </section>

        {/* Social Proof */}
        <div className="mb-16 text-center">
          <p className="text-sm text-gray-600 italic">
            {content.socialProof}
          </p>
        </div>

        {/* 5) AGENCY PILOT */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {content.pilot.title}
          </h2>
          <Card className="p-10 max-w-3xl mx-auto bg-gradient-to-br from-blue-50/50 to-white border-2 border-blue-100">
            <ul className="space-y-4 mb-10">
              {content.pilot.description.map((item, index) => (
                <li key={index} className="text-lg text-gray-700 flex items-start">
                  <span className="mr-4 flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@riverblue.io?subject=Agency%20Pilot%20Application&body=Hi%20RiverBlue%20team,%0D%0A%0D%0AI%27d%20like%20to%20apply%20for%20an%20agency%20pilot.%0D%0A%0D%0AAgency%20name:%0D%0AWebsite:%0D%0AVertical%20(Real%20Estate%20/%20Medspa):%0D%0AHow%20many%20active%20clients%20do%20you%20manage?%0D%0ADo%20your%20clients%20use%20HubSpot%20or%20Salesforce?%0D%0ABest%20contact%20name%20%2B%20role:%0D%0ABest%20phone%20(optional):%0D%0A%0D%0AThanks,"
                className="px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 text-center transform hover:scale-105"
              >
                {content.pilot.cta.primary}
              </a>
              <a
                href="mailto:info@riverblue.io?subject=White-Label%20Eligibility%20Request&body=Hi%20RiverBlue%20team,%0D%0A%0D%0AWe%27re%20interested%20in%20white-label%20eligibility.%0D%0A%0D%0AAgency%20name:%0D%0AWebsite:%0D%0AVertical%20(Real%20Estate%20/%20Medspa):%0D%0ACurrent%20paying%20clients:%0D%0AExpected%20%23%20of%20clients%20you%20could%20onboard%20in%2090%20days:%0D%0ABest%20contact%20name%20%2B%20role:%0D%0A%0D%0AThanks,"
                className="px-8 py-4 bg-white text-gray-700 font-semibold rounded-lg border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-all shadow-md hover:shadow-lg text-center"
              >
                {content.pilot.cta.secondary}
              </a>
            </div>
          </Card>
        </section>
      </main>
    </div>
  );
};
